// client/src/pages/Generate/Generate.jsx 수정 버전

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/main/MainLayout';
import ReportCard from '../../components/ReportCard/ReportCard';
import ReportAddButton from '../../components/ReportAddButton/ReportAddButton';
import ReportInput from '../../components/ReportInput/ReportInput';
import * as S from './Generate.style';
import { inputDataAPI, defaultTemplateAPI } from '../../utils/axios'; // 🆕 Input Data API 추가

const Generate = () => {
  const navigate = useNavigate();
  
  const [inputDataList, setInputDataList] = useState([]); // 🆕 reports -> inputDataList로 변경
  const [isAddingInputData, setIsAddingInputData] = useState(false); // 🆕 변경
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templateStatus, setTemplateStatus] = useState(null);
  const [templateStatusLoading, setTemplateStatusLoading] = useState(false);
  const [templateUpdatingName, setTemplateUpdatingName] = useState('');
  const [templateUpdateMessage, setTemplateUpdateMessage] = useState('');

  // 🆕 Input Data 목록 및 기본 서식 상태 로드
  useEffect(() => {
    loadInputDataList();
    loadDefaultTemplateStatus();
  }, []);

  const loadInputDataList = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('[GENERATE]', 'Loading input data list...');
      
      const result = await inputDataAPI.getList();
      
      if (result.success) {
        setInputDataList(result.data.input_data_list || []);
        console.log('[GENERATE]', 'Input data list loaded:', result.data.input_data_list?.length || 0);
      } else {
        throw new Error(result.message);
      }
      
    } catch (err) {
      console.error('[GENERATE]', 'Load input data error:', err);
      setError('Input Data 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadDefaultTemplateStatus = async (showLoading = true) => {
    if (showLoading) {
      setTemplateStatusLoading(true);
    }

    try {
      const result = await defaultTemplateAPI.getStatus();

      if (result.success) {
        setTemplateStatus(result.data);
        return result.data;
      }

      throw new Error(result.message);
    } catch (err) {
      console.error('[GENERATE]', 'Load default template status error:', err);
      setTemplateUpdateMessage('기본 서식 상태를 확인하지 못했습니다.');
      return null;
    } finally {
      if (showLoading) {
        setTemplateStatusLoading(false);
      }
    }
  };

  const wait = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

  const handleDefaultTemplateUpdate = async (templateName) => {
    if (!templateName) {
      setTemplateUpdateMessage('업데이트할 기본 서식을 선택해주세요.');
      return;
    }

    const templateToUpdate = templateStatus?.templates
      ?.find(template => template.name === templateName);

    if (!templateToUpdate?.update_available) {
      setTemplateUpdateMessage(`${templateName}은(는) 업데이트할 필요가 없습니다.`);
      return;
    }

    const confirmed = window.confirm(
      `${templateName}을(를) 최신 기본 서식으로 업데이트합니다.\n\n업데이트 중에는 기존 서식이 유지되며, 완료 후 새 서식이 적용됩니다.`
    );

    if (!confirmed) {
      return;
    }

    setTemplateUpdatingName(templateName);
    setTemplateUpdateMessage(`${templateName} 업데이트를 시작했습니다.`);

    try {
      const updateResult = await defaultTemplateAPI.update([templateName]);

      if (!updateResult.success) {
        throw new Error(updateResult.message);
      }

      const jobId = updateResult.data?.job_id;

      if (!jobId) {
        await loadDefaultTemplateStatus(false);
        await loadInputDataList(false);
        setTemplateUpdateMessage(updateResult.message || '업데이트할 기본 서식이 없습니다.');
        return;
      }

      for (let attempt = 0; attempt < 60; attempt += 1) {
        await wait(10000);

        const jobResult = await defaultTemplateAPI.getUpdateJob(jobId);
        if (jobResult.success) {
          const job = jobResult.data?.job;
          if (job?.status === 'failed') {
            throw new Error(job.message || '서식 업데이트가 실패했습니다.');
          }

          if (job?.status === 'completed') {
            await loadDefaultTemplateStatus(false);
            await loadInputDataList(false);
            setTemplateUpdateMessage(`${templateName} 업데이트가 완료되었습니다.`);
            return;
          }
        }

        const latestStatus = await loadDefaultTemplateStatus(false);
        if (latestStatus && !latestStatus.any_update_available) {
          await loadInputDataList(false);
          setTemplateUpdateMessage(`${templateName} 업데이트가 완료되었습니다.`);
          return;
        }

        setTemplateUpdateMessage(`${templateName} 업데이트 중입니다. (${attempt + 1}/60)`);
      }

      setTemplateUpdateMessage('서식 업데이트가 오래 걸리고 있습니다. 잠시 후 새로고침으로 상태를 확인해주세요.');
    } catch (err) {
      console.error('[GENERATE]', 'Default template update error:', err);
      setTemplateUpdateMessage(err.message || '기본 서식 업데이트 중 오류가 발생했습니다.');
    } finally {
      setTemplateUpdatingName('');
      await loadDefaultTemplateStatus(false);
      await loadInputDataList(false);
    }
  };

  const handleGenerate = (inputDataId, inputDataInfo) => {
    console.log('[GENERATE]', 'Generate report:', inputDataId, inputDataInfo);
    
    // 🔧 전달되는 데이터 구조 확인 및 수정
    const dataToPass = {
      input_data_id: inputDataId,  // 🆕 명시적으로 ID 전달
      name: inputDataInfo.name,
      description: inputDataInfo.description,
      tables_count: inputDataInfo.tables_count,
      tables_info: inputDataInfo.tables_info,
      filename: inputDataInfo.filename,
      uploaded_at: inputDataInfo.uploaded_at
    };
    
    console.log('[GENERATE]', 'Data being passed to GenerateReport:', dataToPass);
    
    // GenerateReport 컴포넌트로 이동하면서 수정된 Input Data 정보 전달
    navigate('/generate/report', {
      state: { inputDataInfo: dataToPass }
    });
  };

  const handleSettings = (inputDataId, inputDataInfo) => {
    console.log('[GENERATE]', 'Settings for input data:', inputDataId, inputDataInfo);
    
    // TODO: Input Data 설정 페이지로 이동 (JSON 파일 편집 등)
    alert(`"${inputDataInfo.name}" 설정 기능은 준비 중입니다.\n\n` +
          `구현 예정 기능:\n` +
          `- Input Data 테이블 정의 편집\n` +
          `- JSON 파일 내보내기/가져오기\n` +
          `- 테이블 구조 미리보기`);
  };

  const handleCopy = async (inputDataId, inputDataInfo) => {
    const newName = prompt('복사할 Input Data의 이름을 입력하세요:', `${inputDataInfo.name}_copy`);
    if (!newName || !newName.trim()) {
      return;
    }

    try {
      console.log('[GENERATE]', 'Copying input data:', inputDataId, 'to:', newName.trim());
      
      // TODO: Input Data 복사 API 구현 필요
      alert('Input Data 복사 기능은 준비 중입니다.');
      
    } catch (error) {
      console.error('[GENERATE]', 'Copy input data error:', error);
      alert('Input Data 복사 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (inputDataId, inputDataInfo) => {
    if (!window.confirm(`Input Data "${inputDataInfo.name}"을(를) 삭제하시겠습니까?`)) {
      return;
    }

    try {
      console.log('[GENERATE]', 'Deleting input data:', inputDataId);
      
      const result = await inputDataAPI.delete(inputDataId);
      
      if (result.success) {
        setInputDataList(prev => prev.filter(inputData => inputData.input_data_id !== inputDataId));
        alert('Input Data가 삭제되었습니다.');
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('[GENERATE]', 'Delete input data error:', error);
      alert('Input Data 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleAddInputDataClick = () => {
    console.log('[GENERATE]', 'Add input data clicked');
    setIsAddingInputData(true);
  };

  const handleInputDataSave = async (newInputData) => {
    console.log('[GENERATE]', 'Input data saved:', newInputData);
    
    try {
      // 새 Input Data를 목록 맨 앞에 추가
      setInputDataList(prev => [newInputData, ...prev]);
      setIsAddingInputData(false);
    } catch (error) {
      console.error('[GENERATE]', 'Save input data error:', error);
      alert('Input Data 저장 중 오류가 발생했습니다.');
    }
  };

  const handleCancelAdd = () => {
    console.log('[GENERATE]', 'Add input data cancelled');
    setIsAddingInputData(false);
  };

  const handleRefresh = () => {
    console.log('[GENERATE]', 'Refreshing input data list and template status');
    loadInputDataList();
    loadDefaultTemplateStatus();
  };

  return (
    <MainLayout>
      <S.GeneratePageContainer>
        {/* 페이지 헤더 */}
        <S.PageHeader>
          <S.HeaderTop>
            <div>
              <S.PageTitle>분석 실행 및 결과보고서 생성</S.PageTitle>
              <S.PageSubtitle>
                     업로드한 데이터를 분석하고 결과표를 생성합니다
              </S.PageSubtitle>
            </div>
            <S.HeaderActions>
              <S.RefreshButton onClick={handleRefresh} disabled={loading}>
                🔄 새로고침
              </S.RefreshButton>
              <S.ReportCount>
                총 {inputDataList.length}개 보고서
              </S.ReportCount>
            </S.HeaderActions>
          </S.HeaderTop>

          <S.TemplateStatusPanel hasUpdate={templateStatus?.any_update_available}>
            <S.TemplateStatusHeader>
              <div>
                <S.TemplateStatusTitle>보고서 서식 상태</S.TemplateStatusTitle>
                <S.TemplateStatusDescription>
                  {templateStatusLoading
                    ? '기본 서식 상태를 확인하고 있습니다.'
                    : templateStatus?.any_update_available
                      ? '서버에 최신 기본 서식이 있습니다. 업데이트 후 결과보고서를 생성하세요.'
                      : '현재 계정의 기본 서식은 최신 상태입니다.'}
                </S.TemplateStatusDescription>
              </div>
            </S.TemplateStatusHeader>

            {templateStatus?.templates && (
              <S.TemplateStatusList>
                {templateStatus.templates.map(template => (
                  <S.TemplateStatusItem key={template.name} updateAvailable={template.update_available}>
                    <S.TemplateName>{template.name}</S.TemplateName>
                    <S.TemplateStatusBadge updateAvailable={template.update_available}>
                      {template.update_available ? '업데이트 가능' : '최신'}
                    </S.TemplateStatusBadge>
                    <S.TemplateMeta>
                      {template.exists ? `${template.tables_count}개 표` : '미생성'}
                      {template.server_hash_prefix && ` · 서버 ${template.server_hash_prefix}`}
                      {template.current_hash_prefix && ` · 현재 ${template.current_hash_prefix}`}
                    </S.TemplateMeta>
                    <S.TemplateUpdateButton
                      onClick={() => handleDefaultTemplateUpdate(template.name)}
                      disabled={templateStatusLoading || Boolean(templateUpdatingName) || !template.update_available}
                    >
                      {templateUpdatingName === template.name ? '업데이트 중...' : '이 서식 업데이트'}
                    </S.TemplateUpdateButton>
                  </S.TemplateStatusItem>
                ))}
              </S.TemplateStatusList>
            )}

            {templateUpdateMessage && (
              <S.TemplateUpdateMessage>{templateUpdateMessage}</S.TemplateUpdateMessage>
            )}
          </S.TemplateStatusPanel>
        </S.PageHeader>

        {/* 메인 콘텐츠 영역 */}
        <S.ContentArea>
          {/* 전체 로딩 오버레이 */}
          {loading && (
            <S.LoadingOverlay>
              <S.LoadingSpinner />
              <S.LoadingText>보고서 목록을 불러오는 중...</S.LoadingText>
            </S.LoadingOverlay>
          )}

          {/* 에러 표시 */}
          {error && (
            <S.ErrorMessage>
              <S.ErrorIcon>⚠️</S.ErrorIcon>
              <S.ErrorText>{error}</S.ErrorText>
              <S.RetryButton onClick={handleRefresh}>
                다시 시도
              </S.RetryButton>
            </S.ErrorMessage>
          )}

          <S.ReportGridContainer>
            {/* Input Data 카드들 */}
            {!loading && !error && inputDataList.map((inputData) => (
              <ReportCard
                key={inputData.input_data_id}
                report={{
                  id: inputData.input_data_id,
                  name: inputData.name,
                  description: inputData.description,
                  sheetsCount: inputData.tables_count,  // 🔧 올바른 필드명 사용
                  updatedAt: inputData.uploaded_at
                }}
                onGenerate={(reportId, reportInfo) => {
                  // 🔧 올바른 데이터 구조로 전달
                  handleGenerate(inputData.input_data_id, inputData);
                }}
                onSettings={(reportId, reportInfo) => {
                  handleSettings(inputData.input_data_id, inputData);
                }}
                onCopy={(reportId, reportInfo) => {
                  handleCopy(inputData.input_data_id, inputData);
                }}
                onDelete={(reportId, reportInfo) => {
                  handleDelete(inputData.input_data_id, inputData);
                }}
              />
            ))}
            
            {/* Input Data 추가 버튼 또는 입력 폼 */}
            {/* {!loading && !error && (
              isAddingInputData ? (
                <ReportInput
                  onSave={handleInputDataSave}
                  onCancel={handleCancelAdd}
                  isInputData={true} // 🆕 Input Data 모드 표시
                />
              ) : (
                <ReportAddButton onClick={handleAddInputDataClick} />
              )
            )} */}
          </S.ReportGridContainer>



          {/* 빈 상태 표시 */}
          {/* {!loading && !error && inputDataList.length === 0 && !isAddingInputData && (
            <S.EmptyState>
              <S.EmptyIcon>📊</S.EmptyIcon>
              <S.EmptyTitle>등록된 Input Data가 없습니다</S.EmptyTitle>
              <S.EmptyDescription>
                첫 번째 Input Data 템플릿을 추가하여 자동화된 분석 보고서 생성을 시작해보세요
              </S.EmptyDescription>
              <S.EmptyButton onClick={handleAddInputDataClick}>
                Input Data 추가하기
              </S.EmptyButton>
            </S.EmptyState>
          )}
        </S.ContentArea> */}

          {/* 빈 상태 표시  - 업로드 마스킹, 계정 초기 업로드 전 임시 화면*/}
          {!loading && !error && inputDataList.length === 0 && !isAddingInputData && (
            <S.LoadingOverlay>
              <S.LoadingSpinner />
              <S.LoadingText>보고서 서식을 생성 중 입니다. 조금만 기다려 주세요... </S.LoadingText>
            </S.LoadingOverlay>
          )}
        </S.ContentArea>

        {/* 도움말 섹션 */}
        {/* <S.HelpSection>
          <S.HelpTitle>📋 Input Data 관리 가이드</S.HelpTitle>
          <S.HelpList>
            <S.HelpItem>
              <S.HelpIcon>➕</S.HelpIcon>
              <S.HelpText>
                <strong>Input Data 추가:</strong> Excel 형태의 보고서 정의 파일을 업로드하여 새로운 보고서 템플릿을 생성할 수 있습니다
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>⚙️</S.HelpIcon>
              <S.HelpText>
                <strong>설정하기:</strong> 기존 Input Data의 테이블 정의를 편집하고 JSON 파일을 내보내거나 가져올 수 있습니다
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>🚀</S.HelpIcon>
              <S.HelpText>
                <strong>생성하기:</strong> Raw 데이터와 Input Data 템플릿을 선택하여 최종 분석 보고서를 생성합니다
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>📋</S.HelpIcon>
              <S.HelpText>
                <strong>복사/삭제:</strong> 기존 Input Data를 복사하거나 삭제하여 템플릿을 효율적으로 관리할 수 있습니다
              </S.HelpText>
            </S.HelpItem>
          </S.HelpList>
        </S.HelpSection> */}
      </S.GeneratePageContainer>
    </MainLayout>
  );
};

export default Generate;